import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Loader2, Link2, AlertTriangle } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * Receivables dedup, second axis (read-only census + manual-entry repair).
 *
 * Buckets br_ltd Xero invoices that survive the existing BR OS elimination
 * (services.receivables_dedup) into (a) carries a discoverable linkage but
 * wasn't excluded, (b) no linkage found. Nothing is proposed or suggested:
 * bucket (b) is a plain list for manual review, and differing invoice
 * numbers between Xero and BR OS are expected there, not evidence of
 * anything. An operator who wants to link a specific pair types both ids
 * by hand below; Apply stamps only that pair.
 */
function fmt(n) {
  if (n === null || n === undefined) return '-';
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function totalsLine(totalsByCurrency) {
  var entries = Object.entries(totalsByCurrency || {});
  if (entries.length === 0) return '0.00';
  return entries.map(function (e) {
    return fmt(e[1].amount_native) + ' ' + e[0];
  }).join(', ');
}

export function BrLtdInvoiceLinkageCensusPanel() {
  var [loading, setLoading] = useState(false);
  var [census, setCensus] = useState(null);
  var [manualInvoiceId, setManualInvoiceId] = useState('');
  var [manualBrosId, setManualBrosId] = useState('');
  var [applying, setApplying] = useState(false);
  var [applyResult, setApplyResult] = useState(null);

  var run = async function () {
    setLoading(true);
    setApplyResult(null);
    try {
      var res = await diagnosticsAPI.brLtdInvoiceLinkageCensus();
      setCensus(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var applyRepair = async function () {
    var invoiceId = manualInvoiceId.trim();
    var brosId = manualBrosId.trim();
    if (!invoiceId || !brosId) return;
    if (!window.confirm(
      'This will stamp xero_invoice_id onto bros_invoices/' + brosId + ' from invoice ' + invoiceId + '. ' +
      'This does not touch any invoice document. The linked invoice leaves "Unpaid invoices" on the ' +
      'next computation only if the Xero TRACE dedup flag is active.'
    )) return;
    setApplying(true);
    try {
      var res = await diagnosticsAPI.applyBrLtdInvoiceLinkageRepair([{ invoice_id: invoiceId, bros_id: brosId }]);
      setApplyResult(res.data);
      toast.success(res.data.message || 'Linkage repair applied.');
      setManualInvoiceId('');
      setManualBrosId('');
      await run();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          br_ltd Receivables Linkage Census (second axis)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Buckets br_ltd "Unpaid invoices" survivors of the existing BR OS elimination into: carries a
          discoverable linkage but wasn't excluded (a), and no linkage found (b). Nothing is proposed or
          suggested -- differing invoice numbers between Xero and BR OS are expected and not evidence of
          anything. Read-only until Apply is pressed on a pair entered by hand below.
        </p>
        <Button size="sm" onClick={run} disabled={loading} data-testid="br-ltd-linkage-census-run">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run census'}
        </Button>

        {census && (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={census.xero_trace_dedup_active ? 'default' : 'outline'}>
                {census.xero_trace_dedup_active ? 'TRACE dedup active' : 'TRACE dedup not activated'}
              </Badge>
              <span className="text-xs text-muted-foreground">
                survivors: {census.survivors.count} ({totalsLine(census.survivors.totals_by_currency)})
              </span>
              <span className="text-xs text-muted-foreground">
                unpaid BR OS invoices reference: {census.unpaid_bros_invoices_reference.count}
                {' '}({totalsLine(census.unpaid_bros_invoices_reference.totals_by_currency)})
              </span>
            </div>

            {/* Bucket a */}
            <div className="border rounded p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-xs flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> (a) Carries BR OS linkage
                </span>
                <span className="font-mono text-xs">{census.bucket_a_carries_bros_linkage.count}</span>
              </div>
              <div className="font-mono text-xs mb-2">{totalsLine(census.bucket_a_carries_bros_linkage.totals_by_currency)}</div>
              {census.bucket_a_carries_bros_linkage.rows.length > 0 && (
                <div className="overflow-x-auto max-h-48 overflow-y-auto">
                  <table className="text-[11px] w-full border-collapse">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-0.5 pr-1">number</th>
                        <th className="py-0.5 pr-1 text-right">amount</th>
                        <th className="py-0.5 pr-1">reason</th>
                        <th className="py-0.5 pr-1">matched bros</th>
                      </tr>
                    </thead>
                    <tbody>
                      {census.bucket_a_carries_bros_linkage.rows.map(function (r, i) {
                        return (
                          <tr key={i} className={'border-b border-border/20' + (r.reason === 'orphan_bros_id_field' || r.reason === 'UNEXPLAINED_elimination_gap' ? ' bg-amber-50 dark:bg-amber-950/30' : '')}>
                            <td className="py-0.5 pr-1 font-mono">{r.invoice_number || r.id}</td>
                            <td className="py-0.5 pr-1 font-mono text-right">{fmt(r.amount)} {r.currency}</td>
                            <td className="py-0.5 pr-1">
                              <Badge variant={r.reason === 'orphan_bros_id_field' || r.reason === 'UNEXPLAINED_elimination_gap' ? 'destructive' : 'outline'} className="text-[10px]">{r.reason}</Badge>
                            </td>
                            <td className="py-0.5 pr-1 font-mono">
                              {r.matched_bros_invoice ? (r.matched_bros_invoice.invoice_number || r.matched_bros_invoice.bros_id) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Bucket b */}
            <div className="border rounded p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-xs">(b) No linkage found (verify manually if unexpected)</span>
                <span className="font-mono text-xs">{census.bucket_b_no_linkage_found.count}</span>
              </div>
              <div className="font-mono text-xs mb-2">{totalsLine(census.bucket_b_no_linkage_found.totals_by_currency)}</div>
              <p className="text-xs text-muted-foreground mb-2">
                Differing invoice numbers between Xero and BR OS are expected here, not evidence of anything.
                Nothing below is a suggestion.
              </p>
              {census.bucket_b_no_linkage_found.rows.length > 0 && (
                <div className="overflow-x-auto max-h-48 overflow-y-auto">
                  <table className="text-[11px] w-full border-collapse">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-0.5 pr-1">number</th>
                        <th className="py-0.5 pr-1">counterparty</th>
                        <th className="py-0.5 pr-1 text-right">amount</th>
                        <th className="py-0.5 pr-1">issue date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {census.bucket_b_no_linkage_found.rows.map(function (r, i) {
                        return (
                          <tr key={i} className="border-b border-border/20">
                            <td className="py-0.5 pr-1 font-mono">{r.invoice_number || r.id}</td>
                            <td className="py-0.5 pr-1">{r.counterparty}</td>
                            <td className="py-0.5 pr-1 font-mono text-right">{fmt(r.amount)} {r.currency}</td>
                            <td className="py-0.5 pr-1 font-mono">{r.issue_date || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Manual-entry Apply: no candidates, no suggestions */}
            <div className="border rounded p-3 space-y-2">
              <span className="font-semibold text-xs">Link a pair by hand</span>
              <p className="text-xs text-muted-foreground">
                Enter an exact invoice id and bros_id to stamp. Nothing is looked up or suggested.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  className="h-7 text-xs w-48"
                  placeholder="invoice_id"
                  value={manualInvoiceId}
                  onChange={function (e) { setManualInvoiceId(e.target.value); }}
                  data-testid="br-ltd-linkage-manual-invoice-id"
                />
                <Input
                  className="h-7 text-xs w-48"
                  placeholder="bros_id"
                  value={manualBrosId}
                  onChange={function (e) { setManualBrosId(e.target.value); }}
                  data-testid="br-ltd-linkage-manual-bros-id"
                />
                <Button
                  size="sm"
                  onClick={applyRepair}
                  disabled={applying || !manualInvoiceId.trim() || !manualBrosId.trim()}
                  data-testid="br-ltd-linkage-apply"
                >
                  {applying ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Apply
                </Button>
              </div>

              {applyResult && (
                <div className="text-xs space-y-1" data-testid="br-ltd-linkage-apply-result">
                  <p className="text-muted-foreground">{applyResult.message}</p>
                  {applyResult.results.map(function (r, i) {
                    return (
                      <div key={i} className="font-mono">
                        {r.invoice_id} / {r.bros_id}: <Badge variant={r.status === 'linked' ? 'default' : 'outline'} className="text-[10px]">{r.status}</Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
